import { Component } from 'react';
import { connect } from 'react-redux';
import I18n from 'I18n';
import classNames from 'classnames';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { getEvent, getIsUngatedForAvailabilityDaysLimit } from 'MeetingsLib/selectors/BookInfoSelectors';
import { shadeColor } from 'MeetingsLib/utils/color';
import { getTextColor } from 'MeetingsBase/utils/color';
import { isDateInPast } from 'MeetingsBase/utils/dates';
import { UNAVAILABLE_REASONS } from 'MeetingsLib/constants/enums';
import { getGroupedAvailableTimes } from 'MeetingsLib/selectors/AvailabilitySelectors';
import { withTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
class DatePickerButton extends Component {
  constructor(...args) {
    super(...args);
    this.isValid = () => {
      const {
        availableDates,
        date,
        isLoadingLinkAvailabilityPage
      } = this.props;
      if (isLoadingLinkAvailabilityPage) {
        return false;
      }
      return availableDates.includes(date);
    };
    this.getClassNames = () => {
      const {
        selected
      } = this.props;
      const valid = this.isValid();
      return classNames('date-picker-btn', valid ? "valid" : "disabled", selected && "selected");
    };
    this.shouldTroubleshoot = () => {
      const {
        date,
        availableDates,
        isTroubleshooting
      } = this.props;
      if (!isTroubleshooting) {
        return false;
      }
      const dayHasAvailability = availableDates.includes(date);
      return !dayHasAvailability;
    };
    this.getUnbookableReason = () => {
      const {
        bookableDates,
        configuredAvailabilityByDate,
        currentTime,
        date,
        troubleshootTimesForSelectedDurationByDay,
        minimumNoticeTimeBusyDays,
        userTimezone,
        groupedAvailableTimes
      } = this.props;
      if (!this.shouldTroubleshoot()) {
        return null;
      }
      if (isDateInPast(date, currentTime, userTimezone)) {
        return UNAVAILABLE_REASONS.PAST;
      }
      if (bookableDates.includes(date) && !groupedAvailableTimes.has(date)) {
        return UNAVAILABLE_REASONS.TIME_SLOTS_UNAVAILABLE;
      }
      if (!bookableDates.includes(date)) {
        return UNAVAILABLE_REASONS.DATE_NOT_CONFIGURED;
      }
      if (!configuredAvailabilityByDate.get(date)) {
        return UNAVAILABLE_REASONS.TIME_NOT_CONFIGURED;
      }
      if (minimumNoticeTimeBusyDays.includes(date)) {
        return UNAVAILABLE_REASONS.MINIMUM_NOTICE;
      }
      const troubleshootTimesForDate = troubleshootTimesForSelectedDurationByDay.get(date);
      const overlapsWithDayLongEvent = troubleshootTimesForDate && troubleshootTimesForDate.find(troubleshootTime => troubleshootTime.reason === UNAVAILABLE_REASONS.ALL_DAY_EVENT);
      if (overlapsWithDayLongEvent) {
        return UNAVAILABLE_REASONS.ALL_DAY_EVENT;
      }
      const isWorkingHours = troubleshootTimesForDate && troubleshootTimesForDate.find(troubleshootTime => troubleshootTime.reason === UNAVAILABLE_REASONS.OUTSIDE_WORKING_HOURS);
      if (isWorkingHours) {
        return UNAVAILABLE_REASONS.OUTSIDE_WORKING_HOURS;
      }
      return null;
    };
    this.getStyle = () => {
      const {
        branding: {
          primaryColor
        },
        selected,
        theme
      } = this.props;
      const primaryTextColor = getTextColor(primaryColor, theme);
      if (selected) {
        return {
          color: primaryColor,
          background: primaryTextColor,
          visibility: this.getVisibility()
        };
      }
      if (this.shouldTroubleshoot() && this.getUnbookableReason()) {
        const shadedColor = shadeColor(primaryColor, 40);
        return {
          color: getTextColor(shadedColor, theme),
          background: shadedColor,
          visibility: this.getVisibility()
        };
      }
      return {
        color: primaryTextColor,
        visibility: this.getVisibility()
      };
    };
    this.getTooltipText = () => {
      const {
        editLinkURL
      } = this.props;
      const unbookableReason = this.getUnbookableReason();
      switch (unbookableReason) {
        case UNAVAILABLE_REASONS.PAST:
          return /*#__PURE__*/_jsx(FormattedMessage, {
            message: "troubleshoot.day.past"
          });
        case UNAVAILABLE_REASONS.OUTSIDE_WORKING_HOURS:
          return /*#__PURE__*/_jsx(FormattedMessage, {
            message: "troubleshoot.day.workingHours"
          });
        case UNAVAILABLE_REASONS.DATE_NOT_CONFIGURED:
          return /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "troubleshoot.day.customRange_jsx",
            options: {
              href: editLinkURL,
              external: true
            },
            elements: {
              Link
            }
          });
        case UNAVAILABLE_REASONS.TIME_NOT_CONFIGURED:
          return /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "troubleshoot.day.unset_jsx",
            options: {
              href: editLinkURL,
              external: true
            },
            elements: {
              Link
            }
          });
        case UNAVAILABLE_REASONS.MINIMUM_NOTICE:
          return /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "troubleshoot.day.minimum_jsx",
            options: {
              href: editLinkURL,
              external: true
            },
            elements: {
              Link
            }
          });
        case UNAVAILABLE_REASONS.ALL_DAY_EVENT:
          return /*#__PURE__*/_jsx(FormattedMessage, {
            message: "troubleshoot.day.dayLong"
          });
        case UNAVAILABLE_REASONS.TIME_SLOTS_UNAVAILABLE:
          return /*#__PURE__*/_jsx(FormattedMessage, {
            message: "troubleshoot.day.noTimeSlots"
          });
        default:
          return null;
      }
    };
  }
  getVisibility() {
    const {
      isUngatedForAvailabilityDaysLimit,
      isDateInCurrentMonth
    } = this.props;
    if (!isUngatedForAvailabilityDaysLimit) {
      return 'visible';
    }
    return !isDateInCurrentMonth ? 'hidden' : 'visible';
  }
  render() {
    const {
      date,
      daysToAdd,
      handleSetEventDate,
      selected
    } = this.props;
    const placement = daysToAdd > 3 ? 'top left' : 'top right';
    const isValid = this.isValid();
    const tabIndex = isValid ? 0 : -1;
    const disabled = !isValid;
    const button = /*#__PURE__*/_jsx("td", {
      "data-selenium-test-disabled": disabled,
      children: /*#__PURE__*/_jsx("button", {
        "data-test-id": isValid ? 'available-date' : 'unavailable-date',
        "aria-controls": "available-times-region",
        "aria-pressed": selected,
        "aria-disabled": disabled,
        "aria-label": I18n.moment(date).format('MMMM Do'),
        tabIndex: tabIndex,
        id: selected ? 'selected-date' : '',
        style: this.getStyle(),
        onClick: () => handleSetEventDate(date),
        onKeyPress: e => e.key === 'Enter' && handleSetEventDate(date),
        className: this.getClassNames(),
        children: /*#__PURE__*/_jsx("span", {
          children: I18n.moment(date).format('D')
        })
      })
    });
    if (!this.props.isTroubleshooting) {
      return button;
    }
    const tooltipText = this.getTooltipText();
    return /*#__PURE__*/_jsx(Tooltip, {
      disabled: !tooltipText,
      placement: placement,
      title: tooltipText,
      use: "longform",
      children: button
    });
  }
}
export default connect((state, props) => ({
  // @ts-expect-error fix type
  selected: getEvent(state).dateString === props.date,
  isUngatedForAvailabilityDaysLimit: getIsUngatedForAvailabilityDaysLimit(state),
  groupedAvailableTimes: getGroupedAvailableTimes(state)
}))(withTheme(DatePickerButton));